import { ChevronLeft, Expand, Pause, Play, Settings2, Shrink, Volume2, VolumeX, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import type { GroupingSettings, ModeConfig } from './speedReadingShared';
import {
  ACCENT_COLOR_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_LABELS,
  FONT_SIZE_OPTIONS,
  METRONOME_AUDIO_SRC,
  ModeIllustration,
  SidebarPreparationCard,
  applySharedExerciseSettings,
  getThemeChromeButtonStyle,
  getThemeChromeTextStyle,
  getThemedBadgeStyle,
  getThemedLabelStyle,
  getThemedOptionButtonStyle,
  getThemedSwatchStyle,
  getAdjacentMetronomeBpm,
  getMetronomeIntervalMs,
  normalizeMetronomeBpm,
  persistSharedExerciseSettings,
  resolveAccentColor,
} from './speedReadingShared';

type EyeExerciseLayoutProps = {
  mode: ModeConfig;
  title: string;
  subtitle: string;
  running: boolean;
  elapsedSeconds: number;
  controls: React.ReactNode;
  sidebar: React.ReactNode;
  stage: React.ReactNode;
  onClose: () => void;
  onToggleRunning: () => void;
  onReset: () => void;
  muted?: boolean;
  onToggleMuted?: () => void;
};

const formatClock = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const SHARED_EYE_APPEARANCE_EVENT = 'speed-reading:shared-eye-appearance';
const EXERCISE_TOOL_BUTTON_CLASS = 'inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#2f7188] transition hover:text-[#174e67]';

const SettingStepper: React.FC<{
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
}> = ({ label, value, onDecrease, onIncrease }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="grid h-12 grid-cols-[minmax(0,1fr)_54px_96px_54px] items-center">
      <div className="border-r border-[#e7eef2] px-4 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</div>
      <button onClick={onDecrease} className="h-full border-r border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]">
        &laquo;
      </button>
      <div className="text-center text-base font-black text-[#5d7380]">{value}</div>
      <button onClick={onIncrease} className="h-full border-l border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]">
        &raquo;
      </button>
    </div>
  </div>
);

const SwitchKnob: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-[0_2px_8px_rgba(43,62,76,0.18)] transition-all ${checked ? 'left-[34px]' : 'left-1'}`}
  />
);

const ToggleRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
  switchSide?: 'left' | 'right';
}> = ({ label, checked, onChange, switchSide = 'right' }) => {
  const switchButton = (
    <button
      onClick={onChange}
      className={`relative h-8 w-16 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#b6d2ad]' : 'bg-[#d7e1e7]'}`}
      aria-pressed={checked}
      type="button"
    >
      <SwitchKnob checked={checked} />
    </button>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
      <div className="flex min-h-14 items-center gap-4 px-4 py-3">
        {switchSide === 'left' ? switchButton : null}
        <span className="flex-1 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</span>
        {switchSide === 'right' ? switchButton : null}
      </div>
    </div>
  );
};

const SettingChoiceGroup: React.FC<{
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, options, value, onChange }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="border-b border-[#e7eef2] px-4 py-3 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</div>
    <div className="grid grid-cols-3 gap-2 p-2">
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`rounded-lg border px-3 py-2 text-sm font-bold transition-colors ${isActive
              ? 'border-[#6b8798] bg-[#edf4f8] text-[#466173]'
              : 'border-[#e3ebef] bg-[#fbfdfe] text-[#8aa0aa] hover:bg-[#f3f8fa]'
              }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

const MetronomeSettingsControl: React.FC<{
  enabled: boolean;
  onToggle: () => void;
  bpm: number;
  onBpmChange: (value: number) => void;
}> = ({ enabled, onToggle, bpm, onBpmChange }) => (
  <div className="space-y-3">
    <ToggleRow label="Metronom Sesleri" checked={enabled} onChange={onToggle} />
    <SettingStepper
      label="Metronom Hızı"
      value={`${normalizeMetronomeBpm(bpm)} BPM`}
      onDecrease={() => onBpmChange(getAdjacentMetronomeBpm(bpm, -1))}
      onIncrease={() => onBpmChange(getAdjacentMetronomeBpm(bpm, 1))}
    />
  </div>
);

const extractSidebarParagraphs = (content: React.ReactNode): string[] => {
  const paragraphs: string[] = [];

  React.Children.forEach(content, (child) => {
    if (typeof child === 'string') {
      const normalized = child.trim();
      if (normalized) paragraphs.push(normalized);
      return;
    }

    if (!React.isValidElement(child)) return;
    paragraphs.push(...extractSidebarParagraphs(child.props?.children));
  });

  return paragraphs;
};

const useSharedEyeMetronomeSettings = (defaultEnabled = true, defaultBpm = 60) => {
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      metronomeEnabled: defaultEnabled,
      metronomeBpm: defaultBpm,
    }),
    [defaultBpm, defaultEnabled],
  );
  const [metronomeEnabled, setMetronomeEnabled] = useState(Boolean(sharedDefaults.metronomeEnabled));
  const [metronomeBpm, setMetronomeBpm] = useState(normalizeMetronomeBpm(sharedDefaults.metronomeBpm as number | undefined, defaultBpm));

  useEffect(() => {
    persistSharedExerciseSettings({ metronomeEnabled, metronomeBpm });
  }, [metronomeBpm, metronomeEnabled]);

  return { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm };
};

const useSharedEyeAccentColor = (defaultValue = 'default') => {
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({ accentColor: defaultValue }),
    [defaultValue],
  );
  const [accentColor, setAccentColor] = useState(String(sharedDefaults.accentColor || defaultValue));

  useEffect(() => {
    const handleAppearanceChange = (event: Event) => {
      const detail = (event as CustomEvent<{ accentColor?: string }>).detail;
      if (!detail?.accentColor) return;
      setAccentColor(detail.accentColor);
    };

    window.addEventListener(SHARED_EYE_APPEARANCE_EVENT, handleAppearanceChange as EventListener);
    return () => window.removeEventListener(SHARED_EYE_APPEARANCE_EVENT, handleAppearanceChange as EventListener);
  }, []);

  return accentColor;
};

const EyeExerciseLayout: React.FC<EyeExerciseLayoutProps> = ({
  mode,
  title,
  subtitle,
  running,
  elapsedSeconds,
  controls,
  sidebar,
  stage,
  onClose,
  onToggleRunning,
  onReset,
  muted = false,
  onToggleMuted,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      fontSize: 'orta',
      fontFamily: 'GoogleSans',
      accentColor: 'default',
      enableCountdown: true,
    }),
    [],
  );
  const [fontSize, setFontSize] = useState<GroupingSettings['fontSize']>((sharedDefaults.fontSize as GroupingSettings['fontSize']) || 'orta');
  const [fontFamily, setFontFamily] = useState(String(sharedDefaults.fontFamily || 'GoogleSans'));
  const [accentColor, setAccentColor] = useState<'default' | 'red' | 'blue' | 'yellow' | 'pink' | 'green'>((sharedDefaults.accentColor as 'default' | 'red' | 'blue' | 'yellow' | 'pink' | 'green') || 'default');
  const [enableCountdown, setEnableCountdown] = useState(Boolean(sharedDefaults.enableCountdown ?? true));
  const [countdown, setCountdown] = useState<number | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const sidebarParagraphs = extractSidebarParagraphs(sidebar);
  const sessionActive = running || countdown !== null;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    persistSharedExerciseSettings({
      fontSize,
      fontFamily,
      accentColor,
      enableCountdown,
    });
    window.dispatchEvent(new CustomEvent(SHARED_EYE_APPEARANCE_EVENT, {
      detail: { fontSize, fontFamily, accentColor, enableCountdown },
    }));
  }, [accentColor, enableCountdown, fontFamily, fontSize]);

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handlePrimaryAction = () => {
    if (countdown !== null) {
      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdown(null);
      return;
    }

    if (running) {
      onToggleRunning();
      return;
    }

    if (enableCountdown) {
      setCountdown(3);
      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
      }

      let nextValue = 3;
      countdownIntervalRef.current = window.setInterval(() => {
        nextValue -= 1;

        if (nextValue <= 0) {
          if (countdownIntervalRef.current !== null) {
            window.clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setCountdown(null);
          onToggleRunning();
          return;
        }

        setCountdown(nextValue);
      }, 1000);
      return;
    }

    onToggleRunning();
  };

  const handleResetAction = () => {
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdown(null);
    onReset();
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="relative flex h-full gap-5 rounded-xl border border-[#e6edf1] bg-[#f8fafb] px-6 py-5">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={onClose} className="text-[#b6c1c7] hover:text-[#6b7f8a]">
                  <ChevronLeft className="h-5 w-5" style={getThemeChromeTextStyle()} />
                </button>
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{title}</div>
              </div>

              <div className="flex items-center gap-1">
                {onToggleMuted ? (
                  <button onClick={onToggleMuted} className={EXERCISE_TOOL_BUTTON_CLASS} title={muted ? 'Sesi Aç' : 'Sesi Kapat'}>
                    {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </button>
                ) : null}
                <button onClick={() => setSettingsOpen(true)} className={EXERCISE_TOOL_BUTTON_CLASS} title="Ayarlar">
                  <Settings2 className="h-5 w-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="h-5 w-5" /> : <Expand className="h-5 w-5" />}
                </button>
                <button onClick={onClose} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#e8edf0] bg-white">
              {!sessionActive && (
                <div className="flex h-full items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 px-8 text-center">
                    {sidebarParagraphs.map((paragraph, index) => (
                      <p
                        key={`${title}-intro-${index}`}
                        className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              {countdown !== null && (
                <div className="flex h-full items-center justify-center bg-white">
                  <span className="text-7xl font-extrabold" style={getThemeChromeTextStyle()}>{countdown}</span>
                </div>
              )}
              {running && stage}
            </div>
          </div>

          <aside className="hidden lg:flex h-full min-h-0 w-[320px] shrink-0 flex-col rounded-xl border border-[#e0eaee] bg-white p-5">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="mt-1 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{title}</h3>
                <p className="mt-3 text-sm leading-6 text-[#92a4ad]">{subtitle}</p>
              </div>

              {sessionActive && (
                <div className="mt-8 grid grid-cols-1 gap-6 text-center">
                  <div>
                    <div className="text-[14px] text-[#b1b8bd]">Geçen Süre</div>
                    <div className="mt-1 text-[28px] leading-none font-black text-[#194e62]">{formatClock(elapsedSeconds)}</div>
                  </div>
                </div>
              )}

              {!sessionActive && sidebarParagraphs.length > 0 ? (
                <SidebarPreparationCard
                  paragraphs={sidebarParagraphs}
                  fontFamily={undefined}
                />
              ) : null}
            </div>

            <div className="shrink-0 pt-4">
              <button
                onClick={handlePrimaryAction}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-bold text-white transition-colors"
                style={getThemeChromeButtonStyle()}
              >
                {sessionActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {sessionActive ? 'Durdur' : 'Başlat'}
              </button>
              {(sessionActive || elapsedSeconds > 0) && (
                <button
                  onClick={handleResetAction}
                  className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#f1f3f5] text-base font-bold text-[#2d6175] transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 backdrop-blur-[4px]">
              <div className="flex max-h-[92vh] w-[980px] flex-col overflow-hidden rounded-xl bg-white p-7 shadow-lg">
                <div className="mb-5 shrink-0 flex items-center justify-between">
                  <div>
                    <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  </div>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-8 overflow-hidden">
                  <div className="space-y-5 pr-2">
                    {controls}
                    <ToggleRow
                      label="Başlamadan önce geriye say"
                      checked={enableCountdown}
                      onChange={() => setEnableCountdown((prev) => !prev)}
                    />
                  </div>
                  <div className="space-y-5 border-l border-[#eef2f4] pl-7 pr-2">
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setFontSize(sizeOption.value)}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setFontFamily(fontOption.value)}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5 overflow-visible px-1 py-1.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setAccentColor(colorOption.value as 'default' | 'red' | 'blue' | 'yellow' | 'pink' | 'green')}
                              className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya üst araç çubuğunu kullanın
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const useElapsedTimer = (running: boolean) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [running]);

  return { elapsedSeconds, setElapsedSeconds };
};

const useMetronomeTick = (muted = false, bpm = 70) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickAtRef = useRef(Number.NEGATIVE_INFINITY);

  const playTick = useCallback(() => {
    if (muted) return;
    if (typeof window === 'undefined') return;
    const now = window.performance.now();
    const minInterval = 60000 / clamp(bpm, 40, 180);
    if (now - lastTickAtRef.current < minInterval - 8) return;
    lastTickAtRef.current = now;

    if (!audioRef.current) {
      audioRef.current = new Audio(METRONOME_AUDIO_SRC);
      audioRef.current.preload = 'auto';
    }

    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => { });
  }, [bpm, muted]);

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  return playTick;
};

export const MetronomeEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [bpm, setBpm] = useState(60);
  const [amplitude, setAmplitude] = useState(84);
  const [travelWidth, setTravelWidth] = useState(340);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);

  useEffect(() => {
    const recalculateWidth = () => {
      const width = stageRef.current?.clientWidth || 920;
      setTravelWidth(Math.max(180, Math.min(430, (width - 160) / 2)));
    };

    recalculateWidth();
    window.addEventListener('resize', recalculateWidth);
    return () => window.removeEventListener('resize', recalculateWidth);
  }, []);

  const cycleDuration = useMemo(() => getMetronomeIntervalMs(bpm) / 1000, [bpm]);

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Metronom ve Göz Sektirme"
      subtitle="Başınızı sabit tutun, sadece gözlerinizle hareket eden ışığı takip edin."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={() => setRunning((prev) => !prev)}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Tempo"
            value={`${normalizeMetronomeBpm(bpm)} BPM`}
            onDecrease={() => setBpm((prev) => getAdjacentMetronomeBpm(prev, -1))}
            onIncrease={() => setBpm((prev) => getAdjacentMetronomeBpm(prev, 1))}
          />

          <label className="block">
            <div className="mb-2 flex items-center justify-between text-sm font-semibold text-[#506874]">
              <span>Göz Sıçrama Mesafesi</span>
              <span>{amplitude}px</span>
            </div>
            <input
              type="range"
              min={48}
              max={150}
              step={2}
              value={amplitude}
              onChange={(event) => setAmplitude(Number(event.target.value))}
              className="w-full"
            />
          </label>
        </>
      )}
      sidebar={(
        <>
          <p>Merkez çizgiyi değil, hareket eden turkuaz noktayı takip edin.</p>
          <p>Nokta yön değiştirirken gözlerinizin hızlı ve kontrollü sıçrama yapması hedeflenir.</p>
          <p>Tempo yükseldikçe saccadic takip zorlaşır. Baş sabit kalmalı, hareket sadece gözlerden gelmelidir.</p>
        </>
      )}
      stage={(
        <div ref={stageRef} className="relative flex h-full items-center justify-center overflow-hidden">
          <div className="absolute inset-x-16 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-[#bdd6df] to-transparent" />
          <div className="absolute left-1/2 top-1/2 h-[380px] w-px -translate-x-1/2 -translate-y-1/2 bg-gradient-to-b from-transparent via-[#e0edf1] to-transparent" />
          <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#7ca5b6] bg-white shadow-[0_0_0_10px_rgba(255,255,255,0.55)]" />

          <motion.div
            animate={running ? { x: [-travelWidth, travelWidth, -travelWidth], y: [0, -amplitude, 0] } : { x: 0, y: 0 }}
            transition={{
              duration: cycleDuration,
              repeat: running ? Infinity : 0,
              ease: 'easeInOut',
            }}
            className="absolute left-1/2 top-1/2"
          >
            <div className="relative h-12 w-12 -translate-x-1/2 -translate-y-1/2">
              <div className="absolute inset-0 rounded-full bg-[#2ab9d6]/20 blur-md" />
              <div className="absolute inset-[6px] rounded-full border border-[#94dff0] bg-[#1eb6d6] shadow-[0_0_32px_rgba(30,182,214,0.45)]" />
            </div>
          </motion.div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 rounded-full border border-[#d7e6eb] bg-white/85 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5d7985] backdrop-blur">
            {running ? 'Takip Sürüyor' : 'Başlatınca animasyon başlar'}
          </div>
        </div>
      )}
    />
  );
};

type SquarePulse = {
  id: number;
  size: number;
  color: string;
  rotation: number;
};

type PointNode = {
  id: number;
  x: number;
  y: number;
};

type TurnDirection = 'clockwise' | 'counterclockwise' | 'mixed';
type ZigzagFlow = 'pulsed' | 'fluid' | 'mixed';

const PERIPHERAL_SYMBOLS = ['2', '5', '8', '▲', '■', '●', '◆', '3', '7', '9'];
const PERIPHERAL_POSITIONS = [
  { x: 12, y: 12 },
  { x: 50, y: 10 },
  { x: 88, y: 12 },
  { x: 10, y: 50 },
  { x: 90, y: 50 },
  { x: 12, y: 88 },
  { x: 50, y: 90 },
  { x: 88, y: 88 },
  { x: 26, y: 24 },
  { x: 74, y: 24 },
  { x: 24, y: 76 },
  { x: 76, y: 76 },
] as const;
const THIRTEEN_POINT_NODES: PointNode[] = [
  { id: 0, x: 500, y: 500 },
  { id: 1, x: 500, y: 120 },
  { id: 2, x: 190, y: 760 },
  { id: 3, x: 810, y: 760 },
  { id: 4, x: 190, y: 320 },
  { id: 5, x: 500, y: 900 },
  { id: 6, x: 810, y: 320 },
  { id: 7, x: 130, y: 500 },
  { id: 8, x: 690, y: 820 },
  { id: 9, x: 690, y: 260 },
  { id: 10, x: 310, y: 820 },
  { id: 11, x: 310, y: 260 },
  { id: 12, x: 870, y: 500 },
];
const THIRTEEN_POINT_SEQUENCE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 0];
const THIRTEEN_POINT_LABEL_GAP = 10;
const THIRTEEN_POINT_DOT_RADIUS = 19;
const THIRTEEN_POINT_LABEL_DISTANCE = THIRTEEN_POINT_DOT_RADIUS + THIRTEEN_POINT_LABEL_GAP;
const THIRTEEN_POINT_LABEL_DIRECTIONS: Record<number, { x: number; y: number }> = {
  0: { x: -0.72, y: -0.72 },
  8: { x: 1, y: 0 },
  10: { x: -0.72, y: -0.72 },
};
const PONTE_FOCUS_POINTS = [
  { x: 400, y: 185 },
  { x: 400, y: 445 },
  { x: 400, y: 700 },
] as const;
const PONTE_GUIDE_SEGMENTS = [
  { y: 185, startY: 70, endY: 315, lineX1: 145, lineX2: 605, verticalTop: 95, verticalBottom: 315 },
  { y: 445, startY: 315, endY: 575, lineX1: 105, lineX2: 650, verticalTop: 315, verticalBottom: 575 },
  { y: 700, startY: 575, endY: 930, lineX1: 115, lineX2: 710, verticalTop: 575, verticalBottom: 930 },
] as const;
const PONTE_BEATS_PER_DIRECTION = PONTE_FOCUS_POINTS.length - 1;
const SCAN_TOKEN_BANK = ['AL', 'BE', 'KI', 'DO', 'SU', 'REN', 'TA', 'LO', 'FI', 'NE', 'PA', 'MI', 'RA', 'SE', 'TU', 'VO'];

const createZigzagNodes = (orientation: 'vertical' | 'horizontal', pointCount: number): PointNode[] => {
  const safeCount = clamp(pointCount, 4, 12);
  const nodes: PointNode[] = [];

  if (orientation === 'vertical') {
    const top = 100;
    const bottom = 900;
    const left = 80;
    const right = 920;
    const step = safeCount === 1 ? 0 : (right - left) / (safeCount - 1);
    for (let index = 0; index < safeCount; index += 1) {
      const x = left + step * index;
      nodes.push({ id: index * 2 + 1, x, y: top });
      nodes.push({ id: index * 2 + 2, x, y: bottom });
    }
    return nodes;
  }

  const left = 110;
  const right = 930;
  const top = 90;
  const bottom = 910;
  const step = safeCount === 1 ? 0 : (bottom - top) / (safeCount - 1);
  for (let index = 0; index < safeCount; index += 1) {
    const y = top + step * index;
    nodes.push({ id: index * 2 + 1, x: left, y });
    nodes.push({ id: index * 2 + 2, x: right, y });
  }
  return nodes;
};

const buildSnakePath = (rows: number, cols: number, direction: 'rows' | 'columns'): number[] => {
  const indices: number[] = [];

  if (direction === 'rows') {
    for (let row = 0; row < rows; row += 1) {
      const rowIndices = Array.from({ length: cols }, (_, col) => row * cols + col);
      if (row % 2 === 1) rowIndices.reverse();
      indices.push(...rowIndices);
    }
    return indices;
  }

  for (let col = 0; col < cols; col += 1) {
    const colIndices = Array.from({ length: rows }, (_, row) => row * cols + col);
    if (col % 2 === 1) colIndices.reverse();
    indices.push(...colIndices);
  }
  return indices;
};

const createScanBoard = (rows: number, cols: number, level: number): string[] => (
  Array.from({ length: rows * cols }, (_, index) => {
    const token = SCAN_TOKEN_BANK[(index + level) % SCAN_TOKEN_BANK.length];
    return `${token}${(index + level) % 10}`;
  })
);

const createVerticalZigzagTwoRing = (): PointNode[] => {
  const leftConnector = { id: 0, x: 90, y: 500 };
  const rightConnector = { id: 999, x: 910, y: 500 };
  const topLeft = 150;
  const topRight = 850;
  const topPeakY = 120;
  const topValleyY = 420;
  const bottomPeakY = 580;
  const bottomValleyY = 880;
  const zigzagCount = 7;
  const step = (topRight - topLeft) / (zigzagCount - 1);

  const topNodes = Array.from({ length: zigzagCount }, (_, index) => ({
    id: index + 1,
    x: topLeft + step * index,
    y: index % 2 === 0 ? topPeakY : topValleyY,
  }));

  const bottomNodes = Array.from({ length: zigzagCount }, (_, index) => ({
    id: zigzagCount + index + 1,
    x: topLeft + step * index,
    y: index % 2 === 0 ? bottomValleyY : bottomPeakY,
  }));

  return [leftConnector, ...topNodes, rightConnector, ...bottomNodes.slice().reverse()];
};

const createHorizontalZigzagTwoLoop = (): PointNode[] => {
  const topLeft = { id: 1, x: 140, y: 80 };
  const topRight = { id: 2, x: 810, y: 80 };
  const leftStart = { id: 3, x: 210, y: 220 };
  const rightTopMid = { id: 4, x: 810, y: 220 };
  const leftRowTwo = { id: 5, x: 210, y: 380 };
  const rightRowTwo = { id: 6, x: 810, y: 380 };
  const leftRowThree = { id: 7, x: 210, y: 540 };
  const rightRowThree = { id: 8, x: 810, y: 540 };
  const leftRowFour = { id: 9, x: 210, y: 700 };
  const rightRowFour = { id: 10, x: 810, y: 700 };
  const bottomLeft = { id: 11, x: 140, y: 860 };

  return [
    topLeft,
    topRight,
    leftStart,
    rightTopMid,
    leftRowTwo,
    rightRowTwo,
    leftRowThree,
    rightRowThree,
    leftRowFour,
    rightRowFour,
    bottomLeft,
  ];
};

export const ThirteenPointEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      animationEnabled: true,
    }),
    [],
  );
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(1);
  const [animationEnabled, setAnimationEnabled] = useState(Boolean(sharedDefaults.animationEnabled ?? true));
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);
  const currentNode = THIRTEEN_POINT_NODES.find((node) => node.id === THIRTEEN_POINT_SEQUENCE[segmentIndex]) || THIRTEEN_POINT_NODES[0];
  const nextNode = THIRTEEN_POINT_NODES.find((node) => node.id === THIRTEEN_POINT_SEQUENCE[(segmentIndex + 1) % THIRTEEN_POINT_SEQUENCE.length]) || THIRTEEN_POINT_NODES[1];

  useEffect(() => {
    persistSharedExerciseSettings({ animationEnabled });
  }, [animationEnabled]);

  useEffect(() => {
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
    }
  }, [durationMinutes, elapsedSeconds]);

  useEffect(() => {
    if (!running) return;

    const timer = window.setInterval(() => {
      if (metronomeEnabled) playTick();
      setSegmentIndex((prev) => (prev + 1) % (THIRTEEN_POINT_SEQUENCE.length - 1));
    }, beatIntervalMs);

    return () => window.clearInterval(timer);
  }, [beatIntervalMs, metronomeEnabled, playTick, running]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        setSegmentIndex(0);
      }
      return !prev;
    });
  };

  return (
    <EyeExerciseLayout
      mode={mode}
      title="13 Nokta"
      subtitle="Başınızı sabit tutun; oklarla yönlendirilen sırayla mavi noktayı gözlerinizle takip edin."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        setSegmentIndex(0);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <ToggleRow
            label="Animasyonu Kapat"
            checked={!animationEnabled}
            onChange={() => setAnimationEnabled((prev) => !prev)}
          />
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Başınızı oynatmadan yalnızca göz hareketleriyle koyu mavi noktayı takip edin.</p>
          <p>Bir sonraki odak noktası açık halka ile vurgulanır. Takip zorlaşırsa metronom hızını düşürün.</p>
          <p>İlk egzersizlerde zorlanmanız normaldir; sıralı takibi bırakmadan devam etmek göz kaslarını hızla güçlendirir.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)]">
          <svg viewBox="0 0 1000 1000" className="h-full max-h-[820px] w-full max-w-[980px]">
            <defs>
              <marker id="thirteen-arrow-soft" markerWidth="9" markerHeight="9" refX="14" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L6,3 L0,6 z" fill="#c7d3d9" />
              </marker>
            </defs>

            {THIRTEEN_POINT_SEQUENCE.slice(0, -1).map((nodeId, index) => {
              const from = THIRTEEN_POINT_NODES.find((node) => node.id === nodeId);
              const to = THIRTEEN_POINT_NODES.find((node) => node.id === THIRTEEN_POINT_SEQUENCE[index + 1]);
              if (!from || !to) return null;

              return (
                <line
                  key={`${from.id}-${to.id}`}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="#d5dde2"
                  strokeWidth={3}
                  opacity={0.8}
                  markerEnd="url(#thirteen-arrow-soft)"
                />
              );
            })}

            {THIRTEEN_POINT_NODES.map((node) => {
              const isCurrent = node.id === currentNode.id;
              const labelDirection = THIRTEEN_POINT_LABEL_DIRECTIONS[node.id] || { x: 0, y: -1 };
              return (
                <g key={node.id}>
                  <text
                    x={node.x + (labelDirection.x * THIRTEEN_POINT_LABEL_DISTANCE)}
                    y={node.y + (labelDirection.y * THIRTEEN_POINT_LABEL_DISTANCE)}
                    textAnchor="middle"
                    paintOrder="stroke"
                    stroke="#ffffff"
                    strokeWidth="8"
                    className="text-[24px] font-bold"
                    fill={isCurrent ? activeAccentColor : '#b1bbc1'}
                  >
                    {node.id}
                  </text>
                  <circle cx={node.x} cy={node.y} r={THIRTEEN_POINT_DOT_RADIUS} fill={isCurrent ? activeAccentColor : '#d3d7db'} />
                </g>
              );
            })}

            {animationEnabled ? (
              <motion.circle
                key={`${currentNode.id}-${nextNode.id}-${segmentIndex}`}
                initial={{ cx: currentNode.x, cy: currentNode.y }}
                animate={{ cx: nextNode.x, cy: nextNode.y }}
                transition={{ duration: beatIntervalMs / 1000, ease: 'linear' }}
                r={THIRTEEN_POINT_DOT_RADIUS}
                fill={activeAccentColor}
                stroke="#ffffff"
                strokeWidth="3"
                opacity="0.98"
              />
            ) : (
              <circle
                cx={currentNode.x}
                cy={currentNode.y}
                r={THIRTEEN_POINT_DOT_RADIUS}
                fill={activeAccentColor}
                stroke="#ffffff"
                strokeWidth="3"
                opacity="0.98"
              />
            )}
          </svg>
        </div>
      )}
    />
  );
};

export const PonteCircularEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const pathRef = useRef<SVGPathElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);
  const progressRef = useRef(0);
  const activeBeatRef = useRef<number | null>(null);
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(1);
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const [showFocusPoints, setShowFocusPoints] = useState(true);
  const [markerPosition, setMarkerPosition] = useState({ x: 560, y: 70 });
  const [activeFocusIndex, setActiveFocusIndex] = useState<number | null>(null);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);
  const oneWayDurationMs = beatIntervalMs * PONTE_BEATS_PER_DIRECTION;

  useEffect(() => {
    if (!running) {
      if (animationRef.current !== null) {
        window.cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      lastTimestampRef.current = null;
      return;
    }

    const animate = (timestamp: number) => {
      if (!pathRef.current) {
        animationRef.current = window.requestAnimationFrame(animate);
        return;
      }

      if (elapsedSeconds >= durationMinutes * 60) {
        setRunning(false);
        return;
      }

      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
      }

      const delta = timestamp - lastTimestampRef.current;
      lastTimestampRef.current = timestamp;
      progressRef.current = (progressRef.current + delta / oneWayDurationMs) % 2;

      const totalLength = pathRef.current.getTotalLength();
      const pathProgress = progressRef.current <= 1 ? progressRef.current : 2 - progressRef.current;
      const point = pathRef.current.getPointAtLength(totalLength * pathProgress);
      setMarkerPosition({ x: point.x, y: point.y });

      const beatIndex = Math.min(
        Math.floor(progressRef.current * PONTE_BEATS_PER_DIRECTION),
        (PONTE_BEATS_PER_DIRECTION * 2) - 1,
      );
      const focusIndex = beatIndex <= PONTE_BEATS_PER_DIRECTION
        ? beatIndex
        : (PONTE_BEATS_PER_DIRECTION * 2) - beatIndex;
      setActiveFocusIndex(focusIndex);

      if (activeBeatRef.current !== beatIndex) {
        activeBeatRef.current = beatIndex;
        if (metronomeEnabled) playTick();
      }

      animationRef.current = window.requestAnimationFrame(animate);
    };

    animationRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (animationRef.current !== null) {
        window.cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      lastTimestampRef.current = null;
    };
  }, [durationMinutes, elapsedSeconds, metronomeEnabled, oneWayDurationMs, playTick, running]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        progressRef.current = 0;
        activeBeatRef.current = null;
        setMarkerPosition({ x: 560, y: 70 });
        setActiveFocusIndex(null);
      }
      return !prev;
    });
  };

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Dairesel"
      subtitle="Odak noktalarına odaklanarak, bağlı olduğu dairesel hareketi gözlerinizle takip edin."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        progressRef.current = 0;
        activeBeatRef.current = null;
        setMarkerPosition({ x: 560, y: 70 });
        setActiveFocusIndex(null);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
          <ToggleRow label="Odak Noktasını Göster" checked={showFocusPoints} onChange={() => setShowFocusPoints((prev) => !prev)} />
        </>
      )}
      sidebar={(
        <>
          <p>Mavi odak noktası kıvrımlı yol üzerinde akar. Başınızı sabit tutup hareketi yalnızca gözlerinizle izleyin.</p>
          <p>Odak noktalarını göster seçeneği açıkken, çizginin orta eksenindeki rehber noktalar hareketli top bu bölgelere geldiğinde aydınlanır.</p>
          <p>Metronom hızı arttıkça dönüşler daha zorlaşır; ilk çalışmalarda orta hızla başlamak daha doğru olur.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)]">
          <svg viewBox="0 0 800 1000" className="h-full max-h-[860px] w-full max-w-[820px]">
            <path
              ref={pathRef}
              d="M560 70 C 250 70, 190 250, 525 325 C 705 365, 685 545, 275 610 C 110 635, 130 855, 590 925"
              fill="none"
              stroke="#a8adb2"
              strokeWidth="6"
              strokeLinecap="round"
            />

            {showFocusPoints && PONTE_FOCUS_POINTS.map((focusPoint, index) => {
              const isActive = activeFocusIndex === index;
              return (
                <g key={`${focusPoint.x}-${focusPoint.y}`}>
                  <circle
                    cx={focusPoint.x}
                    cy={focusPoint.y}
                    r={isActive ? 18 : 13}
                    fill={isActive ? `${activeAccentColor}22` : '#eef3f6'}
                    opacity={isActive ? 1 : 0.95}
                  />
                  <circle
                    cx={focusPoint.x}
                    cy={focusPoint.y}
                    r={isActive ? 8 : 5}
                    fill={isActive ? activeAccentColor : '#d9e3e8'}
                  />
                </g>
              );
            })}

            <circle
              cx={markerPosition.x}
              cy={markerPosition.y}
              r="24"
              fill={activeAccentColor}
              stroke="#ffffff"
              strokeWidth="5"
              opacity="0.98"
            />
          </svg>
        </div>
      )}
    />
  );
};

export const GrowingSquaresExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [pulseEveryMs, setPulseEveryMs] = useState(620);
  const [growthScale, setGrowthScale] = useState(6.4);
  const [pulses, setPulses] = useState<SquarePulse[]>([]);
  const pulseIdRef = useRef(0);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);

  useEffect(() => {
    if (!running) return;

    const timer = window.setInterval(() => {
      const nextId = pulseIdRef.current + 1;
      pulseIdRef.current = nextId;
      const nextPulse: SquarePulse = {
        id: nextId,
        size: 72 + (nextId % 4) * 14,
        color: activeAccentColor,
        rotation: ((nextId % 6) - 3) * 6,
      };
      setPulses((prev) => [...prev.slice(-6), nextPulse]);

      window.setTimeout(() => {
        setPulses((prev) => prev.filter((pulse) => pulse.id !== nextId));
      }, 2400);
    }, pulseEveryMs);

    return () => window.clearInterval(timer);
  }, [activeAccentColor, pulseEveryMs, running]);

  useEffect(() => {
    if (!running) {
      setPulses([]);
    }
  }, [running]);

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Büyüyen Sonsuz Kareler"
      subtitle="Merkez noktaya bakın; dışa doğru büyüyen kareleri çevresel görüşle algılamaya çalışın."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={() => setRunning((prev) => !prev)}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        setPulses([]);
        pulseIdRef.current = 0;
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Kare Yayılma Sıklığı"
            value={`${pulseEveryMs}ms`}
            onDecrease={() => setPulseEveryMs((prev) => clamp(prev - 50, 260, 1000))}
            onIncrease={() => setPulseEveryMs((prev) => clamp(prev + 50, 260, 1000))}
          />
          <SettingStepper
            label="Büyüme Katsayısı"
            value={`${growthScale.toFixed(1)}x`}
            onDecrease={() => setGrowthScale((prev) => clamp(Number((prev - 0.2).toFixed(1)), 3, 9))}
            onIncrease={() => setGrowthScale((prev) => clamp(Number((prev + 0.2).toFixed(1)), 3, 9))}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Merkezdeki beyaz noktadan gözünüzü ayırmadan yayılan kareleri fark etmeye çalışın.</p>
          <p>Karelerin çevreye doğru büyümesi, çevresel algıyı ve odak sabitlemeyi birlikte çalıştırır.</p>
          <p>Hızlı ayarlar daha yoğun uyarım üretir; düşük ayarlar daha sakin günlük pratik için uygundur.</p>
        </>
      )}
      stage={(
        <div className="relative flex h-full items-center justify-center overflow-hidden bg-white">

          {pulses.map((pulse) => (
            <motion.div
              key={pulse.id}
              initial={{ scale: 0.12, opacity: 0.7, rotate: pulse.rotation }}
              animate={{ scale: growthScale, opacity: 0, rotate: pulse.rotation + 12 }}
              transition={{ duration: 2.35, ease: 'linear' }}
              className="absolute left-1/2 top-1/2"
              style={{
                width: pulse.size,
                height: pulse.size,
                marginLeft: -(pulse.size / 2),
                marginTop: -(pulse.size / 2),
                border: `3px solid ${pulse.color}`,
                boxShadow: `0 0 24px ${activeAccentColor}24`,
              }}
            />
          ))}

          <div
            className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border bg-white shadow-[0_0_0_12px_rgba(255,255,255,0.72)]"
            style={{ borderColor: activeAccentColor }}
          />
          <div className="absolute left-1/2 top-1/2 h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#d9e8ee] opacity-70" />

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 rounded-full border border-[#d7e6eb] bg-white/85 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5d7985] backdrop-blur">
            {running ? 'Merkez Noktaya Bakın' : 'Başlatınca kareler yayılır'}
          </div>
        </div>
      )}
    />
  );
};

type PeripheralStimulus = {
  id: number;
  content: string;
  x: number;
  y: number;
  color: string;
  scale: number;
};

export const FocusCornersExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [spawnEveryMs, setSpawnEveryMs] = useState(1200);
  const [visibleForMs, setVisibleForMs] = useState(900);
  const [stimulusCount, setStimulusCount] = useState(4);
  const [stimuli, setStimuli] = useState<PeripheralStimulus[]>([]);
  const stimuliIdRef = useRef(0);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);

  useEffect(() => {
    if (!running) return;

    const timer = window.setInterval(() => {
      const shuffled = [...PERIPHERAL_POSITIONS].sort(() => Math.random() - 0.5).slice(0, stimulusCount);
      const palette = ['#43b9d5', '#8f9cf0', '#f0a95a', '#72c8a6', '#f17b7b'];
      const nextIdBase = stimuliIdRef.current + 1;
      stimuliIdRef.current = nextIdBase + shuffled.length;

      const nextStimuli = shuffled.map((position, index) => ({
        id: nextIdBase + index,
        content: PERIPHERAL_SYMBOLS[Math.floor(Math.random() * PERIPHERAL_SYMBOLS.length)],
        x: position.x,
        y: position.y,
        color: palette[(nextIdBase + index) % palette.length],
        scale: 0.9 + Math.random() * 0.5,
      }));

      setStimuli(nextStimuli);
      window.setTimeout(() => {
        setStimuli((prev) => prev.filter((item) => !nextStimuli.some((candidate) => candidate.id === item.id)));
      }, visibleForMs);
    }, spawnEveryMs);

    return () => window.clearInterval(timer);
  }, [running, spawnEveryMs, stimulusCount, visibleForMs]);

  useEffect(() => {
    if (!running) setStimuli([]);
  }, [running]);

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Odaklan ve Köşeleri Gör"
      subtitle="Merkez noktaya bakın; köşe ve çevrede beliren işaretleri gözünüzü kaçırmadan fark etmeye çalışın."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={() => setRunning((prev) => !prev)}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        setStimuli([]);
        stimuliIdRef.current = 0;
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <label className="block">
            <div className="mb-2 flex items-center justify-between text-sm font-semibold text-[#506874]">
              <span>Belirme Aralığı</span>
              <span>{spawnEveryMs} ms</span>
            </div>
            <input
              type="range"
              min={500}
              max={2200}
              step={50}
              value={spawnEveryMs}
              onChange={(event) => setSpawnEveryMs(Number(event.target.value))}
              className="w-full"
            />
          </label>

          <label className="block">
            <div className="mb-2 flex items-center justify-between text-sm font-semibold text-[#506874]">
              <span>Görünür Kalma Süresi</span>
              <span>{visibleForMs} ms</span>
            </div>
            <input
              type="range"
              min={350}
              max={1600}
              step={50}
              value={visibleForMs}
              onChange={(event) => setVisibleForMs(Number(event.target.value))}
              className="w-full"
            />
          </label>

          <label className="block">
            <div className="mb-2 flex items-center justify-between text-sm font-semibold text-[#506874]">
              <span>Eşzamanlı İşaret Sayısı</span>
              <span>{stimulusCount}</span>
            </div>
            <input
              type="range"
              min={2}
              max={8}
              step={1}
              value={stimulusCount}
              onChange={(event) => setStimulusCount(Number(event.target.value))}
              className="w-full"
            />
          </label>
        </>
      )}
      sidebar={(
        <>
          <p>Gözünüzü ortadaki sabit noktada tutun; çevrede beliren sayı ve şekilleri çevresel görüşle fark etmeye çalışın.</p>
          <p>Bu mod, merkezi odağı bozmadan köşelerdeki uyarıları algılama becerisini güçlendirir.</p>
          <p>Daha kısa görünür kalma süresi ve daha fazla işaret, egzersizi belirgin şekilde zorlaştırır.</p>
        </>
      )}
      stage={(
        <div className="relative flex h-full items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.98)_0%,_rgba(235,244,247,0.95)_48%,_rgba(220,234,241,0.92)_100%)]" />

          {stimuli.map((stimulus) => (
            <motion.div
              key={stimulus.id}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: stimulus.scale }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="absolute select-none text-[2.1rem] font-black leading-none whitespace-nowrap"
              style={{
                left: `${stimulus.x}%`,
                top: `${stimulus.y}%`,
                color: stimulus.color,
                transform: 'translate(-50%, -50%)',
                textShadow: `0 0 26px ${stimulus.color}20`,
                willChange: 'transform, opacity',
              }}
            >
              {stimulus.content}
            </motion.div>
          ))}

          <div className="absolute left-1/2 top-1/2 h-[220px] w-[220px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#d9e8ee] opacity-55" />
          <div className="absolute left-1/2 top-1/2 h-[120px] w-[120px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#e1edf1] opacity-75" />
          <div className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#638697] bg-white shadow-[0_0_0_16px_rgba(255,255,255,0.8)]" />

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 rounded-full border border-[#d7e6eb] bg-white/88 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5d7985] backdrop-blur">
            {running ? 'Merkezden Ayrılmayın' : 'Başlatınca çevresel işaretler görünür'}
          </div>
        </div>
      )}
    />
  );
};

const ZigzagEyeExerciseViewBase: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
  title: string;
  subtitle: string;
  orientation: 'vertical' | 'horizontal';
}> = ({ mode, onClose, title, subtitle, orientation }) => {
  const useHorizontalZigzagSettings = orientation === 'horizontal';
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      exerciseDurationSeconds: 60,
      pointCount: 7,
      markCorners: true,
    }),
    [],
  );
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(
    useHorizontalZigzagSettings
      ? clamp(Math.round((Number(sharedDefaults.exerciseDurationSeconds) || 60) / 60), 1, 10)
      : 1,
  );
  const [pointCount, setPointCount] = useState(
    useHorizontalZigzagSettings
      ? clamp(Number(sharedDefaults.pointCount) || 7, 4, 12)
      : 7,
  );
  const [markCorners, setMarkCorners] = useState(
    useHorizontalZigzagSettings
      ? Boolean(sharedDefaults.markCorners ?? true)
      : true,
  );
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);
  const nodes = useMemo(() => createZigzagNodes(orientation, pointCount), [orientation, pointCount]);
  const traversalPath = useMemo(() => {
    if (nodes.length <= 1) return [0];

    return [
      ...Array.from({ length: nodes.length }, (_, index) => index),
      ...Array.from({ length: Math.max(0, nodes.length - 2) }, (_, index) => nodes.length - 2 - index),
    ];
  }, [nodes.length]);
  const currentNode = nodes[traversalPath[segmentIndex] || 0] || nodes[0];
  const firstTopNode = orientation === 'vertical' ? nodes[0] : null;
  const firstBottomNode = orientation === 'vertical' ? nodes[1] : null;
  const lastTopNode = orientation === 'vertical' ? nodes[nodes.length - 2] : null;
  const lastBottomNode = orientation === 'vertical' ? nodes[nodes.length - 1] : null;
  const firstRowLeftNode = orientation === 'horizontal' ? nodes[0] : null;
  const lastRowRightNode = orientation === 'horizontal' ? nodes[nodes.length - 1] : null;

  useEffect(() => {
    setSegmentIndex(0);
  }, [orientation, pointCount]);

  useEffect(() => {
    if (!useHorizontalZigzagSettings) return;

    persistSharedExerciseSettings({
      exerciseDurationSeconds: durationMinutes * 60,
      pointCount,
      markCorners,
    });
  }, [durationMinutes, markCorners, pointCount, useHorizontalZigzagSettings]);

  useEffect(() => {
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
    }
  }, [durationMinutes, elapsedSeconds]);

  useEffect(() => {
    if (!running) return;
    if (traversalPath.length <= 1) return;

    const timer = window.setInterval(() => {
      if (metronomeEnabled) playTick();
      setSegmentIndex((prev) => (prev + 1) % traversalPath.length);
    }, beatIntervalMs);

    return () => window.clearInterval(timer);
  }, [beatIntervalMs, metronomeEnabled, playTick, running, traversalPath.length]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        setSegmentIndex(0);
      }
      return !prev;
    });
  };

  return (
    <EyeExerciseLayout
      mode={mode}
      title={title}
      subtitle={subtitle}
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        setSegmentIndex(0);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <SettingStepper
            label="Nokta Sayısı"
            value={`${pointCount}`}
            onDecrease={() => setPointCount((prev) => clamp(prev - 1, 4, 12))}
            onIncrease={() => setPointCount((prev) => clamp(prev + 1, 4, 12))}
          />
          {useHorizontalZigzagSettings ? (
            <ToggleRow label="Köşeleri İşaretle" checked={markCorners} onChange={() => setMarkCorners((prev) => !prev)} />
          ) : null}
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Başınızı oynatmadan koyu mavi hedefi yalnızca göz hareketleriyle takip edin.</p>
          <p>Zikzak hattı yön değiştirdikçe göz sıçramaları hızlanır; zorlanırsanız metronom hızını düşürün.</p>
          <p>Daha fazla nokta, daha uzun satır ve daha sık dönüş anlamına gelir. İlk çalışmalarda orta seviyede başlamak daha uygundur.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)]">
          <svg viewBox="0 0 1000 1000" className="h-full max-h-[820px] w-full max-w-[980px]">
            <defs>
              <marker id={`zigzag-arrow-${orientation}`} markerWidth="10" markerHeight="10" refX="5.5" refY="2.5" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L5.5,2.5 L0,5 z" fill="#cfd7dc" />
              </marker>
            </defs>

            {nodes.slice(0, -1).map((node, index) => {
              const to = nodes[index + 1];
              return (
                <line
                  key={`${node.id}-${to.id}`}
                  x1={node.x}
                  y1={node.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="#d6dee3"
                  strokeWidth={3}
                  opacity={0.9}
                />
              );
            })}

            {firstTopNode && firstBottomNode ? (
              <line
                x1={firstTopNode.x}
                y1={firstTopNode.y + 55}
                x2={firstBottomNode.x}
                y2={firstTopNode.y + 145}
                stroke="#d6dee3"
                strokeWidth={3}
                markerEnd={`url(#zigzag-arrow-${orientation})`}
                opacity={0.9}
              />
            ) : null}

            {firstRowLeftNode ? (
              <line
                x1={firstRowLeftNode.x + 22}
                y1={firstRowLeftNode.y}
                x2={firstRowLeftNode.x + 78}
                y2={firstRowLeftNode.y}
                stroke="#d6dee3"
                strokeWidth={3}
                markerEnd={`url(#zigzag-arrow-${orientation})`}
                opacity={0.9}
              />
            ) : null}

            {lastTopNode && lastBottomNode ? (
              <line
                x1={lastBottomNode.x}
                y1={lastBottomNode.y - 55}
                x2={lastTopNode.x}
                y2={lastBottomNode.y - 145}
                stroke="#d6dee3"
                strokeWidth={3}
                markerEnd={`url(#zigzag-arrow-${orientation})`}
                opacity={0.9}
              />
            ) : null}

            {lastRowRightNode ? (
              <line
                x1={lastRowRightNode.x - 22}
                y1={lastRowRightNode.y}
                x2={lastRowRightNode.x - 78}
                y2={lastRowRightNode.y}
                stroke="#d6dee3"
                strokeWidth={3}
                markerEnd={`url(#zigzag-arrow-${orientation})`}
                opacity={0.9}
              />
            ) : null}

            {nodes.map((node) => {
              const isCurrent = node.id === currentNode.id;
              const isVisible = useHorizontalZigzagSettings ? (isCurrent || markCorners) : true;

              if (!isVisible) return null;

              return (
                <g key={node.id}>
                  <circle cx={node.x} cy={node.y} r={isCurrent ? 22 : 17} fill={isCurrent ? activeAccentColor : '#d6dce0'} />
                </g>
              );
            })}
          </svg>
        </div>
      )}
    />
  );
};

export const VerticalZigzagEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => (
  <ZigzagEyeExerciseViewBase
    mode={mode}
    onClose={onClose}
    title="Dikey Zikzak"
    subtitle="Başınızı sabit tutun; dikey akışta zikzak yapan hedefi gözlerinizle takip edin."
    orientation="vertical"
  />
);

export const HorizontalZigzagEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => (
  <ZigzagEyeExerciseViewBase
    mode={mode}
    onClose={onClose}
    title="Yatay Zikzak"
    subtitle="Başınızı sabit tutun; yatay akışta zikzak yapan hedefi gözlerinizle takip edin."
    orientation="horizontal"
  />
);

export const VerticalZigzagTwoEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      exerciseDurationSeconds: 60,
      verticalZigzagTwoTurnDirection: 'clockwise',
      verticalZigzagTwoFlow: 'pulsed',
    }),
    [],
  );
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(clamp(Math.round((Number(sharedDefaults.exerciseDurationSeconds) || 60) / 60), 1, 10));
  const [turnDirection, setTurnDirection] = useState<TurnDirection>(
    sharedDefaults.verticalZigzagTwoTurnDirection === 'counterclockwise' || sharedDefaults.verticalZigzagTwoTurnDirection === 'mixed'
      ? sharedDefaults.verticalZigzagTwoTurnDirection
      : 'clockwise',
  );
  const [flow, setFlow] = useState<ZigzagFlow>(
    sharedDefaults.verticalZigzagTwoFlow === 'fluid' || sharedDefaults.verticalZigzagTwoFlow === 'mixed'
      ? sharedDefaults.verticalZigzagTwoFlow
      : 'pulsed',
  );
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const ringNodes = useMemo(() => createVerticalZigzagTwoRing(), []);
  const [activePosition, setActivePosition] = useState(() => ({ x: ringNodes[1]?.x || 0, y: ringNodes[1]?.y || 0 }));
  const cycleIndexRef = useRef(0);
  const segmentIndexRef = useRef(1);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);

  useEffect(() => {
    persistSharedExerciseSettings({
      exerciseDurationSeconds: durationMinutes * 60,
      verticalZigzagTwoTurnDirection: turnDirection,
      verticalZigzagTwoFlow: flow,
    });
  }, [durationMinutes, flow, turnDirection]);

  useEffect(() => {
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
    }
  }, [durationMinutes, elapsedSeconds]);

  useEffect(() => {
    setActivePosition({ x: ringNodes[1]?.x || 0, y: ringNodes[1]?.y || 0 });
    cycleIndexRef.current = 0;
    segmentIndexRef.current = 1;
  }, [ringNodes, turnDirection, flow, beatIntervalMs]);

  useEffect(() => {
    if (!running) return;
    if (ringNodes.length <= 1) return;

    let frameId = 0;
    let lastFrame = 0;
    let elapsedInSegment = 0;
    let currentSegment = segmentIndexRef.current;
    let currentCycle = cycleIndexRef.current;

    const computeVariant = (cycle: number) => ({
      direction: turnDirection === 'mixed'
        ? (cycle % 2 === 0 ? 'clockwise' : 'counterclockwise')
        : turnDirection,
      flowMode: flow === 'mixed'
        ? (cycle % 2 === 0 ? 'pulsed' : 'fluid')
        : flow,
    });

    const getTraversalNodes = (direction: TurnDirection) => (
      direction === 'counterclockwise' ? [ringNodes[0], ...ringNodes.slice(1).reverse()] : ringNodes
    );

    const updatePosition = (segmentElapsed: number, cycle: number, segment: number) => {
      const variant = computeVariant(cycle);
      const traversalNodes = getTraversalNodes(variant.direction);
      const fromNode = traversalNodes[segment % traversalNodes.length];
      const toNode = traversalNodes[(segment + 1) % traversalNodes.length];
      const progress = variant.flowMode === 'fluid' ? clamp(segmentElapsed / beatIntervalMs, 0, 1) : 0;

      setActivePosition({
        x: fromNode.x + ((toNode.x - fromNode.x) * progress),
        y: fromNode.y + ((toNode.y - fromNode.y) * progress),
      });
    };

    updatePosition(0, currentCycle, currentSegment);

    const animate = (timestamp: number) => {
      if (!lastFrame) lastFrame = timestamp;
      const delta = timestamp - lastFrame;
      lastFrame = timestamp;
      elapsedInSegment += delta;

      while (elapsedInSegment >= beatIntervalMs) {
        elapsedInSegment -= beatIntervalMs;
        currentSegment = (currentSegment + 1) % ringNodes.length;
        if (currentSegment === 0) {
          currentCycle += 1;
          cycleIndexRef.current = currentCycle;
        }
        segmentIndexRef.current = currentSegment;
        if (metronomeEnabled) playTick();
      }

      updatePosition(elapsedInSegment, currentCycle, currentSegment);
      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frameId);
  }, [beatIntervalMs, flow, metronomeEnabled, playTick, ringNodes, running, turnDirection]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        cycleIndexRef.current = 0;
        segmentIndexRef.current = 1;
        setActivePosition({ x: ringNodes[1]?.x || 0, y: ringNodes[1]?.y || 0 });
      }
      return !prev;
    });
  };

  const topPath = ringNodes.slice(1, 8);
  const rightConnector = [ringNodes[7], ringNodes[8], ringNodes[9]];
  const bottomPath = ringNodes.slice(9);
  const leftConnector = [ringNodes[ringNodes.length - 1], ringNodes[0], ringNodes[1]];

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Dikey Zikzak 2"
      subtitle="Kapalı zikzak hattında dönen hedefi yalnızca göz hareketleriyle takip edin."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        cycleIndexRef.current = 0;
        segmentIndexRef.current = 1;
        setActivePosition({ x: ringNodes[1]?.x || 0, y: ringNodes[1]?.y || 0 });
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <SettingChoiceGroup
            label="Dönüş Yönü"
            value={turnDirection}
            onChange={(value) => setTurnDirection(value as TurnDirection)}
            options={[
              { value: 'clockwise', label: 'Saat Yönü' },
              { value: 'counterclockwise', label: 'Saat Yönü Tersi' },
              { value: 'mixed', label: 'Karışık' },
            ]}
          />
          <SettingChoiceGroup
            label="Akış"
            value={flow}
            onChange={(value) => setFlow(value as ZigzagFlow)}
            options={[
              { value: 'pulsed', label: 'Darbeli' },
              { value: 'fluid', label: 'Akışkan' },
              { value: 'mixed', label: 'Karışık' },
            ]}
          />
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Hedef, üst ve alt zikzak bantları arasında kapalı bir rota üzerinde dolaşır; başınızı oynatmadan yalnızca gözlerinizle izleyin.</p>
          <p>Dönüş yönü ayarı, hattın saat yönünde mi tersinde mi akacağını belirler. Karışık mod her tam turda yön değiştirir.</p>
          <p>Akış darbeli olduğunda hedef düğüm düğüm sıçrar, akışkan olduğunda çizgi boyunca kayar. Karışık mod iki davranışı sırayla uygular.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)]">
          <svg viewBox="0 0 1000 1000" className="h-full max-h-[820px] w-full max-w-[980px]">
            <polyline
              points={topPath.map((node) => `${node.x},${node.y}`).join(' ')}
              fill="none"
              stroke="#d9e1e6"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.95}
            />
            <polyline
              points={rightConnector.map((node) => `${node.x},${node.y}`).join(' ')}
              fill="none"
              stroke="#d9e1e6"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.95}
            />
            <polyline
              points={bottomPath.map((node) => `${node.x},${node.y}`).join(' ')}
              fill="none"
              stroke="#d9e1e6"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.95}
            />
            <polyline
              points={leftConnector.map((node) => `${node.x},${node.y}`).join(' ')}
              fill="none"
              stroke="#d9e1e6"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.95}
            />

            <circle cx={activePosition.x} cy={activePosition.y} r={20} fill={activeAccentColor} opacity={0.18} />
            <circle cx={activePosition.x} cy={activePosition.y} r={14} fill={activeAccentColor} />
          </svg>
        </div>
      )}
    />
  );
};

export const HorizontalZigzagTwoEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      exerciseDurationSeconds: 60,
      horizontalZigzagTwoTurnDirection: 'clockwise',
      horizontalZigzagTwoFlow: 'pulsed',
    }),
    [],
  );
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(clamp(Math.round((Number(sharedDefaults.exerciseDurationSeconds) || 60) / 60), 1, 10));
  const [turnDirection, setTurnDirection] = useState<TurnDirection>(
    sharedDefaults.horizontalZigzagTwoTurnDirection === 'counterclockwise' || sharedDefaults.horizontalZigzagTwoTurnDirection === 'mixed'
      ? sharedDefaults.horizontalZigzagTwoTurnDirection
      : 'clockwise',
  );
  const [flow, setFlow] = useState<ZigzagFlow>(
    sharedDefaults.horizontalZigzagTwoFlow === 'fluid' || sharedDefaults.horizontalZigzagTwoFlow === 'mixed'
      ? sharedDefaults.horizontalZigzagTwoFlow
      : 'pulsed',
  );
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const loopNodes = useMemo(() => createHorizontalZigzagTwoLoop(), []);
  const [activePosition, setActivePosition] = useState(() => ({ x: loopNodes[0]?.x || 0, y: loopNodes[0]?.y || 0 }));
  const cycleIndexRef = useRef(0);
  const segmentIndexRef = useRef(0);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);

  useEffect(() => {
    persistSharedExerciseSettings({
      exerciseDurationSeconds: durationMinutes * 60,
      horizontalZigzagTwoTurnDirection: turnDirection,
      horizontalZigzagTwoFlow: flow,
    });
  }, [durationMinutes, flow, turnDirection]);

  useEffect(() => {
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
    }
  }, [durationMinutes, elapsedSeconds]);

  useEffect(() => {
    setActivePosition({ x: loopNodes[0]?.x || 0, y: loopNodes[0]?.y || 0 });
    cycleIndexRef.current = 0;
    segmentIndexRef.current = 0;
  }, [beatIntervalMs, flow, loopNodes, turnDirection]);

  useEffect(() => {
    if (!running) return;
    if (loopNodes.length <= 1) return;

    let frameId = 0;
    let lastFrame = 0;
    let elapsedInSegment = 0;
    let currentSegment = segmentIndexRef.current;
    let currentCycle = cycleIndexRef.current;

    const computeVariant = (cycle: number) => ({
      direction: turnDirection === 'mixed'
        ? (cycle % 2 === 0 ? 'clockwise' : 'counterclockwise')
        : turnDirection,
      flowMode: flow === 'mixed'
        ? (cycle % 2 === 0 ? 'pulsed' : 'fluid')
        : flow,
    });

    const getTraversalNodes = (direction: TurnDirection) => (
      direction === 'counterclockwise' ? [loopNodes[0], ...loopNodes.slice(1).reverse()] : loopNodes
    );

    const updatePosition = (segmentElapsed: number, cycle: number, segment: number) => {
      const variant = computeVariant(cycle);
      const traversalNodes = getTraversalNodes(variant.direction);
      const fromNode = traversalNodes[segment % traversalNodes.length];
      const toNode = traversalNodes[(segment + 1) % traversalNodes.length];
      const progress = variant.flowMode === 'fluid' ? clamp(segmentElapsed / beatIntervalMs, 0, 1) : 0;

      setActivePosition({
        x: fromNode.x + ((toNode.x - fromNode.x) * progress),
        y: fromNode.y + ((toNode.y - fromNode.y) * progress),
      });
    };

    updatePosition(0, currentCycle, currentSegment);

    const animate = (timestamp: number) => {
      if (!lastFrame) lastFrame = timestamp;
      const delta = timestamp - lastFrame;
      lastFrame = timestamp;
      elapsedInSegment += delta;

      while (elapsedInSegment >= beatIntervalMs) {
        elapsedInSegment -= beatIntervalMs;
        currentSegment = (currentSegment + 1) % loopNodes.length;
        if (currentSegment === 0) {
          currentCycle += 1;
          cycleIndexRef.current = currentCycle;
        }
        segmentIndexRef.current = currentSegment;
        if (metronomeEnabled) playTick();
      }

      updatePosition(elapsedInSegment, currentCycle, currentSegment);
      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frameId);
  }, [beatIntervalMs, flow, loopNodes, metronomeEnabled, playTick, running, turnDirection]);

  const loopPath = [...loopNodes, loopNodes[0]];

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Yatay Zikzak 2"
      subtitle="Kapalı yatay zikzak hattında dönen hedefi yalnızca göz hareketleriyle takip edin."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={() => {
        setRunning((prev) => {
          if (!prev && elapsedSeconds >= durationMinutes * 60) {
            setElapsedSeconds(0);
            cycleIndexRef.current = 0;
            segmentIndexRef.current = 0;
            setActivePosition({ x: loopNodes[0]?.x || 0, y: loopNodes[0]?.y || 0 });
          }
          return !prev;
        });
      }}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        cycleIndexRef.current = 0;
        segmentIndexRef.current = 0;
        setActivePosition({ x: loopNodes[0]?.x || 0, y: loopNodes[0]?.y || 0 });
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <SettingChoiceGroup
            label="Dönüş Yönü"
            value={turnDirection}
            onChange={(value) => setTurnDirection(value as TurnDirection)}
            options={[
              { value: 'clockwise', label: 'Saat Yönü' },
              { value: 'counterclockwise', label: 'Saat Yönü Tersi' },
              { value: 'mixed', label: 'Karışık' },
            ]}
          />
          <SettingChoiceGroup
            label="Akış"
            value={flow}
            onChange={(value) => setFlow(value as ZigzagFlow)}
            options={[
              { value: 'pulsed', label: 'Darbeli' },
              { value: 'fluid', label: 'Akışkan' },
              { value: 'mixed', label: 'Karışık' },
            ]}
          />
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Hedef, yatay bantlar arasında kapalı bir rota üzerinde dolaşır; başınızı oynatmadan yalnızca gözlerinizle izleyin.</p>
          <p>Dönüş yönü ayarı, hattın saat yönünde mi tersinde mi akacağını belirler. Karışık mod her tam turda yön değiştirir.</p>
          <p>Akış darbeli olduğunda hedef düğüm düğüm sıçrar, akışkan olduğunda çizgi boyunca kayar. Karışık mod iki davranışı sırayla uygular.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)]">
          <svg viewBox="0 0 1000 1000" className="h-full max-h-[820px] w-full max-w-[980px]">
            <polyline
              points={loopPath.map((node) => `${node.x},${node.y}`).join(' ')}
              fill="none"
              stroke="#d9e1e6"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.95}
            />

            <circle cx={activePosition.x} cy={activePosition.y} r={20} fill={activeAccentColor} opacity={0.18} />
            <circle cx={activePosition.x} cy={activePosition.y} r={14} fill={activeAccentColor} />
          </svg>
        </div>
      )}
    />
  );
};

export const HourglassEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const HOURGLASS_LETTER_OPTIONS = ['A', 'B', 'C', 'Ç', 'D', 'E', 'F', 'G', 'Ğ', 'H', 'I', 'İ', 'J', 'K', 'L', 'M', 'N', 'O', 'Ö', 'P', 'R', 'S', 'Ş', 'T', 'U', 'Ü', 'V', 'Y', 'Z'] as const;
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      exerciseDurationSeconds: 60,
      showFocusPoint: true,
      showLetters: true,
      letterCount: 5,
      letterSizePx: 21,
      selectedLetters: ['B'],
    }),
    [],
  );
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(clamp(Math.round((Number(sharedDefaults.exerciseDurationSeconds) || 60) / 60), 1, 10));
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const [showFocusPoint, setShowFocusPoint] = useState(Boolean(sharedDefaults.showFocusPoint ?? true));
  const [showLetters, setShowLetters] = useState(Boolean(sharedDefaults.showLetters ?? true));
  const [letterCount, setLetterCount] = useState(clamp(Number(sharedDefaults.letterCount) || 5, 1, 8));
  const [letterSizePx, setLetterSizePx] = useState(clamp(Number(sharedDefaults.letterSizePx) || 21, 12, 48));
  const [selectedLetters, setSelectedLetters] = useState<string[]>(
    Array.isArray(sharedDefaults.selectedLetters) && sharedDefaults.selectedLetters.length > 0
      ? sharedDefaults.selectedLetters.map((letter) => String(letter).toUpperCase())
      : ['B'],
  );
  const [activePairStep, setActivePairStep] = useState(0);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const accentColor = useSharedEyeAccentColor();
  const activeAccentColor = resolveAccentColor(accentColor);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);

  useEffect(() => {
    persistSharedExerciseSettings({
      exerciseDurationSeconds: durationMinutes * 60,
      showFocusPoint,
      showLetters,
      letterCount,
      letterSizePx,
      selectedLetters,
    });
  }, [durationMinutes, showFocusPoint, showLetters, letterCount, letterSizePx, selectedLetters]);

  useEffect(() => {
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
    }
  }, [durationMinutes, elapsedSeconds]);

  useEffect(() => {
    setActivePairStep(0);
  }, [letterCount, selectedLetters]);

  const hourglassGeometry = useMemo(() => {
    const topLeft = { x: 170, y: 80 };
    const topRight = { x: 830, y: 80 };
    const center = { x: 500, y: 500 };
    const bottomLeft = { x: 170, y: 920 };
    const bottomRight = { x: 830, y: 920 };

    const createOffsetPoint = (
      start: { x: number; y: number },
      end: { x: number; y: number },
      t: number,
      offset: number,
    ) => {
      const x = start.x + (end.x - start.x) * t;
      const y = start.y + (end.y - start.y) * t;
      const length = Math.hypot(end.x - start.x, end.y - start.y) || 1;
      const normalX = -(end.y - start.y) / length;
      const normalY = (end.x - start.x) / length;

      return {
        x: x + normalX * offset,
        y: y + normalY * offset,
      };
    };

    const letters = selectedLetters.length > 0 ? selectedLetters : ['B'];
    const createBand = (
      start: { x: number; y: number },
      end: { x: number; y: number },
      offset: number,
      key: string,
    ) => Array.from({ length: letterCount }, (_, index) => ({
      id: `${key}-${index}`,
      letter: letters[index % letters.length],
      index,
      ...createOffsetPoint(start, end, (index + 1) / (letterCount + 1), offset),
    }));

    return {
      topLeft,
      topRight,
      center,
      bottomLeft,
      bottomRight,
      topLeftBand: createBand(topLeft, center, 30, 'tl'),
      topRightBand: createBand(topRight, center, -30, 'tr'),
      bottomLeftBand: createBand(bottomLeft, center, -30, 'bl'),
      bottomRightBand: createBand(bottomRight, center, 30, 'br'),
      cornerLetters: [
        { id: 'corner-tl', letter: letters[0 % letters.length], x: topLeft.x - 28, y: topLeft.y + 18 },
        { id: 'corner-tr', letter: letters[1 % letters.length], x: topRight.x + 28, y: topRight.y + 18 },
        { id: 'corner-bl', letter: letters[2 % letters.length], x: bottomLeft.x - 28, y: bottomLeft.y - 18 },
        { id: 'corner-br', letter: letters[3 % letters.length], x: bottomRight.x + 28, y: bottomRight.y - 18 },
      ],
    };
  }, [letterCount, selectedLetters]);

  const activeRoute = useMemo(() => {
    const createRouteItem = (
      id: string,
      left: { id: string; x: number; y: number; letter: string },
      right: { id: string; x: number; y: number; letter: string },
    ) => ({
      id,
      left,
      right,
      focusY: (left.y + right.y) / 2,
    });

    const descending = [
      createRouteItem('route-top-corners', hourglassGeometry.cornerLetters[0], hourglassGeometry.cornerLetters[1]),
      ...hourglassGeometry.topLeftBand.map((leftItem, index) => (
        createRouteItem(`route-top-${index}`, leftItem, hourglassGeometry.topRightBand[index])
      )),
      ...hourglassGeometry.bottomLeftBand.slice().reverse().map((leftItem, index) => (
        createRouteItem(
          `route-bottom-${index}`,
          leftItem,
          hourglassGeometry.bottomRightBand[hourglassGeometry.bottomRightBand.length - 1 - index],
        )
      )),
      createRouteItem('route-bottom-corners', hourglassGeometry.cornerLetters[2], hourglassGeometry.cornerLetters[3]),
    ];

    if (descending.length <= 1) return descending;

    return [
      ...descending,
      ...descending.slice(1, -1).reverse(),
    ];
  }, [hourglassGeometry]);

  const activeRouteItem = activeRoute[activePairStep % activeRoute.length] || activeRoute[0] || null;
  const activeLetterIds = useMemo(() => new Set(
    [activeRouteItem?.left?.id, activeRouteItem?.right?.id].filter(Boolean)
  ), [activeRouteItem]);

  useEffect(() => {
    setActivePairStep(0);
  }, [activeRoute.length]);

  useEffect(() => {
    if (!running) return;

    const stepCount = Math.max(1, activeRoute.length);

    const timer = window.setInterval(() => {
      if (metronomeEnabled) playTick();
      setActivePairStep((prev) => (prev + 1) % stepCount);
    }, beatIntervalMs);

    return () => window.clearInterval(timer);
  }, [activeRoute.length, beatIntervalMs, metronomeEnabled, playTick, running]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        setActivePairStep(0);
      }
      return !prev;
    });
  };

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Kum Saati"
      subtitle="Kum saati çizgisinde ilerleyen hedefi gözlerinizle takip edin; merkez geçişlerde odağı kaybetmeyin."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        setActivePairStep(0);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
          <ToggleRow label="Odak Noktasını Göster" checked={showFocusPoint} onChange={() => setShowFocusPoint((prev) => !prev)} />
          <ToggleRow label="Harfleri İşaretle" checked={showLetters} onChange={() => setShowLetters((prev) => !prev)} />
          <SettingStepper
            label="Harf Sayısı"
            value={`${letterCount}`}
            onDecrease={() => setLetterCount((prev) => clamp(prev - 1, 1, 8))}
            onIncrease={() => setLetterCount((prev) => clamp(prev + 1, 1, 8))}
          />
          <SettingStepper
            label="Harf Büyüklüğü"
            value={`${letterSizePx}px`}
            onDecrease={() => setLetterSizePx((prev) => clamp(prev - 1, 12, 48))}
            onIncrease={() => setLetterSizePx((prev) => clamp(prev + 1, 12, 48))}
          />
          <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
            <div className="border-b border-[#e7eef2] px-4 py-3 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">
              Harfler
            </div>
            <div className="grid grid-cols-8 gap-2 p-3">
              {HOURGLASS_LETTER_OPTIONS.map((letter) => {
                const selected = selectedLetters.includes(letter);
                return (
                  <button
                    key={letter}
                    type="button"
                    onClick={() => {
                      setSelectedLetters((prev) => {
                        if (prev.includes(letter)) {
                          if (prev.length === 1) return prev;
                          return prev.filter((item) => item !== letter);
                        }

                        return [...prev, letter].slice(0, 8);
                      });
                    }}
                    className={`flex h-11 items-center justify-center rounded-lg border-2 text-lg font-bold transition-colors ${selected ? 'border-[#71879a] bg-[#dbe5ec] text-[#61798c]' : 'border-[#d7dee3] bg-white text-[#98a8b2] hover:border-[#bcc8cf]'}`}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
      sidebar={(
        <>
          <p>Merkezdeki odak noktasını korurken kum saati çizgilerinin çevresine yerleştirilmiş harfleri çevresel görüşle takip edin.</p>
          <p>Metronom hızı, vurgulanan harf çiftlerinin ne kadar hızlı değişeceğini belirler. Zorlanırsanız BPM değerini düşürün.</p>
          <p>Tek harf seçerseniz referanstaki gibi aynı harf tekrarlanır; birden fazla harf seçerseniz kenarlara dönüşümlü dağılır.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)]">
          <svg viewBox="0 0 1000 1000" className="h-full max-h-[820px] w-full max-w-[980px]">
            <line x1={hourglassGeometry.topLeft.x} y1={hourglassGeometry.topLeft.y} x2={hourglassGeometry.topRight.x} y2={hourglassGeometry.topRight.y} stroke="#969da3" strokeWidth="5" />
            <line x1={hourglassGeometry.bottomLeft.x} y1={hourglassGeometry.bottomLeft.y} x2={hourglassGeometry.bottomRight.x} y2={hourglassGeometry.bottomRight.y} stroke="#969da3" strokeWidth="5" />
            <line x1={hourglassGeometry.topLeft.x} y1={hourglassGeometry.topLeft.y} x2={hourglassGeometry.bottomRight.x} y2={hourglassGeometry.bottomRight.y} stroke="#969da3" strokeWidth="5" />
            <line x1={hourglassGeometry.topRight.x} y1={hourglassGeometry.topRight.y} x2={hourglassGeometry.bottomLeft.x} y2={hourglassGeometry.bottomLeft.y} stroke="#969da3" strokeWidth="5" />

            {showFocusPoint ? (
              <>
                <circle cx={500} cy={activeRouteItem?.focusY ?? hourglassGeometry.center.y} r="16" fill="rgba(255,255,255,0.92)" />
                <circle cx={500} cy={activeRouteItem?.focusY ?? hourglassGeometry.center.y} r="7" fill={activeAccentColor} opacity="0.45" />
              </>
            ) : null}

            {showLetters ? (
              <>
                {[activeRouteItem?.left, activeRouteItem?.right]
                  .filter(Boolean)
                  .map((item) => (
                    <circle key={`highlight-${item.id}`} cx={item.x} cy={item.y} r={letterSizePx * 0.95} fill={activeAccentColor} opacity="0.18" />
                  ))}
                {[...hourglassGeometry.cornerLetters, ...hourglassGeometry.topLeftBand, ...hourglassGeometry.topRightBand, ...hourglassGeometry.bottomLeftBand, ...hourglassGeometry.bottomRightBand].map((item) => (
                  <text
                    key={item.id}
                    x={item.x}
                    y={item.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={activeLetterIds.has(item.id) ? activeAccentColor : '#92989d'}
                    style={{ fontSize: `${letterSizePx}px`, fontWeight: 700 }}
                  >
                    {item.letter}
                  </text>
                ))}
              </>
            ) : null}
          </svg>
        </div>
      )}
    />
  );
};

const ScanBoardExerciseViewBase: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
  title: string;
  subtitle: string;
  pathDirection: 'rows' | 'columns';
}> = ({ mode, onClose, title, subtitle, pathDirection }) => {
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(1);
  const [level, setLevel] = useState(4);
  const { metronomeEnabled, setMetronomeEnabled, metronomeBpm, setMetronomeBpm } = useSharedEyeMetronomeSettings(true, 60);
  const [activePathIndex, setActivePathIndex] = useState(0);
  const playTick = useMetronomeTick(muted, metronomeBpm);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const beatIntervalMs = getMetronomeIntervalMs(metronomeBpm);
  const rows = pathDirection === 'rows' ? 5 : 6;
  const cols = pathDirection === 'rows' ? 6 : 5;
  const cells = useMemo(() => createScanBoard(rows, cols, level), [cols, level, rows]);
  const path = useMemo(() => buildSnakePath(rows, cols, pathDirection), [cols, pathDirection, rows]);
  const activeCellIndex = path[activePathIndex % path.length] || 0;

  useEffect(() => {
    setActivePathIndex(0);
  }, [level, pathDirection]);

  useEffect(() => {
    if (!running) return;
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
      return;
    }

    const timer = window.setTimeout(() => {
      if (metronomeEnabled) playTick();
      setActivePathIndex((prev) => (prev + 1) % path.length);
    }, beatIntervalMs);

    return () => window.clearTimeout(timer);
  }, [beatIntervalMs, durationMinutes, elapsedSeconds, metronomeEnabled, path.length, playTick, running]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        setActivePathIndex(0);
      }
      return !prev;
    });
  };

  return (
    <EyeExerciseLayout
      mode={mode}
      title={title}
      subtitle={subtitle}
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        setActivePathIndex(0);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <SettingStepper
            label="Seviye"
            value={`${level}`}
            onDecrease={() => setLevel((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setLevel((prev) => clamp(prev + 1, 1, 10))}
          />
          <MetronomeSettingsControl
            enabled={metronomeEnabled}
            onToggle={() => setMetronomeEnabled((prev) => !prev)}
            bpm={metronomeBpm}
            onBpmChange={setMetronomeBpm}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Her vurguda yalnızca aktif hücreye bakın ve çizilen tarama düzenini başınızı oynatmadan sürdürün.</p>
          <p>Metronom hızı yükseldikçe geçişler hızlanır; seviye hücre içeriğinin zorluğunu belirler.</p>
          <p>Bu yapı, metin satırlarına geçmeden önce göz kaslarını ritimli tarama düzenine alıştırmak için tasarlandı.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)] px-10">
          <div
            className="grid w-full max-w-[860px] gap-4"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {cells.map((cell, index) => {
              const isActive = index === activeCellIndex;
              return (
                <div
                  key={`${cell}-${index}`}
                  className={`flex h-20 items-center justify-center rounded-2xl border text-xl font-black tracking-[0.2em] transition-all ${isActive
                    ? 'border-[#5f7b8d] bg-[#e9f3f7] text-[#315366] shadow-[0_12px_28px_rgba(49,83,102,0.16)] scale-[1.02]'
                    : 'border-[#e2ebef] bg-white text-[#a3b0b8]'
                    }`}
                >
                  {cell}
                </div>
              );
            })}
          </div>
        </div>
      )}
    />
  );
};

export const GuidedVerticalZigzagExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => (
  <ScanBoardExerciseViewBase
    mode={mode}
    onClose={onClose}
    title="Dikey Zikzak"
    subtitle="Kolonlar arasında dikey zikzak düzeninde ilerleyen hücreleri gözlerinizle sırayla tarayın."
    pathDirection="columns"
  />
);

export const GuidedHorizontalZigzagExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => (
  <ScanBoardExerciseViewBase
    mode={mode}
    onClose={onClose}
    title="Yatay Zikzak"
    subtitle="Satırlar boyunca yatay zikzak düzeninde ilerleyen hücreleri gözlerinizle ritimli biçimde takip edin."
    pathDirection="rows"
  />
);

export const ColumnsEyeExerciseView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const { systemConfig } = useAuth();
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const sharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      exerciseDurationSeconds: 60,
      jumpDurationMs: 500,
    }),
    [],
  );
  const [durationMinutes, setDurationMinutes] = useState(clamp(Math.round((Number(sharedDefaults.exerciseDurationSeconds) || 60) / 60), 1, 10));
  const [jumpDurationMs, setJumpDurationMs] = useState(clamp(Number(sharedDefaults.jumpDurationMs) || 500, 100, 500));
  const [activeCellIndex, setActiveCellIndex] = useState(0);
  const { elapsedSeconds, setElapsedSeconds } = useElapsedTimer(running);
  const activeCellIndexRef = useRef(0);
  const accentColor = useSharedEyeAccentColor();
  const rowCount = 10;
  const columnCount = 3;
  const companyLabel = String(systemConfig?.companyName || 'Yeni Eğitim Sistemi').trim() || 'Yeni Eğitim Sistemi';
  const cells = useMemo(() => Array.from({ length: rowCount * columnCount }, () => companyLabel), [companyLabel]);
  const activeAccentColor = resolveAccentColor(accentColor);
  const companyFontSizePx = useMemo(() => {
    const length = companyLabel.length;
    if (length <= 12) return 32;
    if (length <= 16) return 28;
    if (length <= 20) return 24;
    if (length <= 24) return 21;
    return 18;
  }, [companyLabel]);

  useEffect(() => {
    persistSharedExerciseSettings({
      exerciseDurationSeconds: durationMinutes * 60,
      jumpDurationMs,
    });
  }, [durationMinutes, jumpDurationMs]);

  useEffect(() => {
    activeCellIndexRef.current = activeCellIndex;
  }, [activeCellIndex]);

  useEffect(() => {
    if (!running) return;
    if (elapsedSeconds >= durationMinutes * 60) {
      setRunning(false);
      return;
    }

    let frameId = 0;
    let lastFrame = 0;
    let elapsedInStep = 0;

    const animate = (timestamp: number) => {
      if (!lastFrame) lastFrame = timestamp;
      const delta = timestamp - lastFrame;
      lastFrame = timestamp;
      elapsedInStep += delta;

      while (elapsedInStep >= jumpDurationMs) {
        elapsedInStep -= jumpDurationMs;
        activeCellIndexRef.current = (activeCellIndexRef.current + 1) % cells.length;
        setActiveCellIndex(activeCellIndexRef.current);
      }

      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frameId);
  }, [cells.length, durationMinutes, elapsedSeconds, jumpDurationMs, running]);

  const handleToggleRunning = () => {
    setRunning((prev) => {
      if (!prev && elapsedSeconds >= durationMinutes * 60) {
        setElapsedSeconds(0);
        activeCellIndexRef.current = 0;
        setActiveCellIndex(0);
      }
      return !prev;
    });
  };

  return (
    <EyeExerciseLayout
      mode={mode}
      title="Kolonlar"
      subtitle="Kolonları başınızı oynatmadan yukarıdan aşağıya ritimli biçimde tarayın."
      running={running}
      elapsedSeconds={elapsedSeconds}
      onClose={onClose}
      onToggleRunning={handleToggleRunning}
      onReset={() => {
        setRunning(false);
        setElapsedSeconds(0);
        activeCellIndexRef.current = 0;
        setActiveCellIndex(0);
      }}
      muted={muted}
      onToggleMuted={() => setMuted((prev) => !prev)}
      controls={(
        <>
          <SettingStepper
            label="Egzersiz Süresi"
            value={`${durationMinutes.toString().padStart(2, '0')}:00`}
            onDecrease={() => setDurationMinutes((prev) => clamp(prev - 1, 1, 10))}
            onIncrease={() => setDurationMinutes((prev) => clamp(prev + 1, 1, 10))}
          />
          <SettingStepper
            label="Sıçrama Süresi"
            value={`${jumpDurationMs}ms`}
            onDecrease={() => setJumpDurationMs((prev) => clamp(prev - 100, 100, 500))}
            onIncrease={() => setJumpDurationMs((prev) => clamp(prev + 100, 100, 500))}
          />
        </>
      )}
      sidebar={(
        <>
          <p>Her vurguda yalnızca seçili vurgu rengine dönen şirket adına bakın ve kolonları başınızı oynatmadan yukarıdan aşağıya takip edin.</p>
          <p>Sıçrama süresi azaldıkça gözün aynı kolon içinde daha hızlı konum değiştirmesi gerekir.</p>
          <p>Gösterilen metin, Ayarlar &gt; Genel Bilgiler bölümündeki şirket veya kurum adından alınır.</p>
        </>
      )}
      stage={(
        <div className="flex h-full items-center justify-center bg-[linear-gradient(180deg,#ffffff_0%,#f6fafc_100%)] px-10">
          <div
            className="grid w-full max-w-[860px] gap-x-16 gap-y-5"
            style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
          >
            {cells.map((cell, index) => {
              const isActive = index === activeCellIndex;
              return (
                <div
                  key={`${cell}-${index}`}
                  className={`flex min-h-[42px] items-center justify-start rounded-xl px-2 tracking-[-0.02em] whitespace-nowrap ${isActive ? 'font-bold' : 'font-normal text-[#6f7378]'}`}
                  style={{
                    fontFamily: 'Georgia, Times New Roman, serif',
                    color: isActive ? activeAccentColor : undefined,
                    fontSize: `${companyFontSizePx}px`,
                    lineHeight: 1.15,
                  }}
                >
                  {cell}
                </div>
              );
            })}
          </div>
        </div>
      )}
    />
  );
};
